/*
 * This file is part of the Det Norske Teatret application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, { useEffect, type ComponentProps } from 'react';
import { useNavigate } from 'react-router';
import classnames from 'classnames';
import useEnhanced from 'effects/enhanced';
import Nav from '../Nav/Nav';
import Header from './Header/Header';
import Next from './Next/Next';
import renderContentBlock from './Content';
import useMenu from './menu';
import useAnimation from './animations';
import './Entry.scss';

const DEFAULT_CONTENT: NonNullable<EntryProps['content']> = [];
const DEFAULT_NAV: NonNullable<EntryProps['nav']> = [];

const DEFAULT_LABELS = {
  name: 'Name',
  menu: 'Menu',
  open: 'Open',
  close: 'Close',
};

const DEFAULT_ON_THEME = () => {};

interface EntryProps {
  type?: ComponentProps<typeof Header>['type'];
  theme?: string;
  excerpt?: string;
  author?: string;
  photographer?: string;
  content?: { type: string; [prop: string]: unknown }[];
  nav?: ComponentProps<typeof Nav>['items'];
  labels?: { name: string; menu: string; open: string; close: string };
  next?: Omit<ComponentProps<typeof Next>, 'onNavigate'> | null;
  onTheme?: (theme: string) => void;
  // The rest goes to the Header.
  [prop: string]: unknown;
}

/**
 * This is the Entry component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 */
function Entry({
  type = 'doubleTitle',
  theme = 'white',
  excerpt,
  author,
  photographer,
  content = DEFAULT_CONTENT,
  nav = DEFAULT_NAV,
  labels = DEFAULT_LABELS,
  next = null,
  onTheme = DEFAULT_ON_THEME,
  ...props
}: EntryProps) {
  const navigate = useNavigate();
  const enhanced = useEnhanced(660);
  const [ref, animate] = useAnimation();
  const menuRef = useMenu();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    onTheme(theme);
  }, [theme]);

  function renderExcerpt() {
    if (type === 'largeImage' && enhanced) return null;
    if (type === 'doubleTitle') return null;
    if (type === 'sideImage') return null;
    if (type === 'bottomImage') return null;
    if (type === 'fullHeightHalfImage') return null;

    return (
      <div className="excerpt" appear="true">
        {['largeImage', 'video'].indexOf(type) > -1 ? (
          <p
            dangerouslySetInnerHTML={{ __html: excerpt as string }}
            stagger-text="false"
          />
        ) : (
          <p dangerouslySetInnerHTML={{ __html: excerpt as string }} />
        )}
      </div>
    );
  }

  function renderCopyright() {
    if (type === 'doubleTitle') return null;
    if (type === 'largeIntro') return null;
    if (type === 'videoEntry') return null;
    if (type === 'sideImage') return null;
    if (type === 'bottomImage') return null;
    if (type === 'fullHeightHalfImage') return null;
    if (type === 'largeImage' && enhanced) return null;

    function renderContent() {
      if (photographer) {
        return (
          <>
            <span className="author">{author}</span>
            {' // '}
            <span className="photo">{photographer}</span>
          </>
        );
      }
      return <span className="author">{author}</span>;
    }

    return (
      <div key="copyright" className="copyright" stagger-text="true">
        {renderContent()}
      </div>
    );
  }

  function renderNext() {
    if (!next) return null;
    return (
      <Next
        {...next}
        onNavigate={url => {
          animate()
            .then(() => {
              navigate(url);
            })
            .catch(() => {
              navigate(url);
            });
        }}
      />
    );
  }

  return (
    <article ref={ref} className={classnames('page entry', type)}>
      <span className="span" />
      <Nav ref={menuRef} items={nav} labels={labels} />
      <Header
        enhanced={enhanced}
        type={type}
        excerpt={excerpt}
        author={author}
        photographer={photographer}
        {...props}
      />
      <main>
        {renderExcerpt()}
        {renderCopyright()}
        <div className="content-blocks">{content.map(renderContentBlock)}</div>
        {renderNext()}
      </main>
    </article>
  );
}

export default Entry;
